import { AmazonianHardwareWithTaxonomyID, Specification } from "@amzn/amazonian-hardware-catalogical";
import { CatalogicalRequestContent } from "common/types/catalogical-type";
import { useGetItem, useGetItems } from "hooks/catalogical-amznham";
import jp from "jsonpath";
import { ItemTaxonomyId } from "lib/taxonomy/item-taxonomy-id";
import { IItemVersion, ItemDetails, ItemTypes } from "lib/types/items";
import { useEffect, useState } from "react";

import { useGetItemDetailsResult } from "./types/itemDetails.types";

const GET_PARENT_INFO_IF_NULL = true;

export const useGetHardwareDetails = (): useGetItemDetailsResult => {
  const { data: itemParent, error: errorGetItem, isLoading: isLoadingGetItem, doGetItem } = useGetItem();
  const { data: itemChildren, error: errorGetItems, isLoading: isLoadingGetItems, doGetItems } = useGetItems();

  const [mappedItem, setMappedItem] = useState<ItemDetails | undefined>(undefined);
  const [defaultVersion, setDefaultVersion] = useState<IItemVersion | undefined>(undefined);
  const [versions, setVersions] = useState<IItemVersion[]>([]);

  useEffect(() => {
    if (itemParent && itemChildren) {
      setHookInfo();
    }
  }, [itemParent, itemChildren]);

  const setHookInfo = () => {
    // Item has versions
    if (itemChildren && itemChildren.length > 0) {
      setMappedItem(mapItem(itemParent!));
      setDefaultVersion(undefined); // Hardware items do not have a pre-selected version, the first selection is up to the user
      setVersions(mapVersions(itemChildren));
    }
    // Use parent item as main product
    else {
      setMappedItem(mapItem(itemParent!));
      setDefaultVersion(mapVersion(itemParent));
    }
  };

  const fetchItem = async (taxonomyId: ItemTaxonomyId) => {
    doGetItem(taxonomyId.getTaxonomyId());

    const params: CatalogicalRequestContent = {
      limit: 500,
      offset: 0,
      sortBy: "name",
      sortDirection: "asc",
      filter: JSON.stringify({ parentTaxonomyId: taxonomyId.getTaxonomyId() }),
    };
    doGetItems({ searchParams: params });
  };

  const mapVersions = (versions: AmazonianHardwareWithTaxonomyID[]): IItemVersion[] => {
    if (!versions || versions.length === 0) {
      return [];
    }
    return versions.map((catalogVersion) => mapVersion(catalogVersion, GET_PARENT_INFO_IF_NULL)!);
  };

  const mapVersion = (
    version: AmazonianHardwareWithTaxonomyID | undefined,
    checkParent: boolean = false
  ): IItemVersion | undefined => {
    if (!version) {
      return undefined;
    }
    return {
      taxonomyId: version.taxonomyId,
      name: version.name,
      displayText: version.model,
      status: getValue(checkParent, version.status, "status"),
      amount: getValue(checkParent, version.price?.amount, "price.amount", 0),
      currency: getValue(checkParent, version.price?.currency, "price.currency"),
      logoUrl: getValue(checkParent, version.logoUrl, "logoUrl"),
    };
  };

  const updateVersionSelected = (selectedVersion: IItemVersion) => {
    if (itemChildren && itemChildren?.length > 0) {
      const newSelectedChild = itemChildren.find((child) => child.taxonomyId === selectedVersion.taxonomyId);
      setDefaultVersion(mapVersion(newSelectedChild, GET_PARENT_INFO_IF_NULL));
      setMappedItem(mapItem(newSelectedChild!, GET_PARENT_INFO_IF_NULL));
    }
  };

  const mapItem = (item: AmazonianHardwareWithTaxonomyID, checkParent: boolean = false): ItemDetails => {
    const os = item.os;
    const categories = [item.subCategory];

    return {
      taxonomyId: item.taxonomyId,
      parentTxId: itemParent?.taxonomyId,
      parentName: itemParent?.name,
      name: getValue(checkParent, item.name, "name"),
      description: getValue(checkParent, item.description, "description"),
      shortDescription: getValue(checkParent, item.shortDescription, "shortDescription"),
      status: getValue(checkParent, item.status, "status"),
      logoUrl: getValue(checkParent, item.logoUrl, "logoUrl"),
      publisher: getValue(checkParent, item.manufacturer, "manufacturer"),
      etaDays: getValue(checkParent, item.etaDays, "etaDays", 0),
      priceAmount: getValue(checkParent, item.price?.amount, "price.amount", 0),
      priceCurrency: getValue(checkParent, item.price?.currency, "price.currency"),
      os: os ? [os] : ["-"],
      categories: categories ?? ["-"],
      hasStock: true,
    };
  };

  const getValue = (checkParent: boolean, value: any, key: string, defaultValue?: any) => {
    const valueOrDefault = value ?? defaultValue ?? "";
    if (!checkParent) {
      return valueOrDefault;
    }

    // Should get value from parent if child value is not present
    if ((!value || value === "") && itemParent && jp.value(itemParent, key)) {
      return jp.value(itemParent, key);
    }

    return valueOrDefault;
  };

  const getInternalSpec = (specs: Specification[], key: string) => {
    const internalSpec = specs.find((spec) => spec.key === key);
    if (internalSpec) {
      const value = internalSpec.unit ? `${internalSpec.value}${internalSpec.unit}` : `${internalSpec.value}`;
      return value;
    }

    return undefined;
  };

  return {
    type: ItemTypes.HARDWARE,
    item: mappedItem,
    isLoading: isLoadingGetItem || isLoadingGetItems,
    error: errorGetItem ?? errorGetItems,
    defaultVersion,
    versions,
    updateVersionSelected,
    getItem: fetchItem,
  };
};
